/**
 * React Query hooks for file operations on sandboxes.
 * Provides declarative data fetching and mutations for file system operations.
 *
 * @module hooks/useFiles
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { fileService } from '@/services';
import { showErrorToast } from '@/utils/errorToast';

/**
 * Query key factory for file operations.
 * Ensures consistent cache key generation.
 */
export const fileKeys = {
  all: (sandboxId: string, containerName?: string) =>
    ['files', sandboxId, containerName ?? ''] as const,
  list: (sandboxId: string, path: string, containerName?: string) =>
    [...fileKeys.all(sandboxId, containerName), 'list', path] as const,
  stat: (sandboxId: string, path: string, containerName?: string) =>
    [...fileKeys.all(sandboxId, containerName), 'stat', path] as const,
};

/**
 * Hook to list files in a directory
 */
export function useListFiles(
  sandboxId: string,
  path: string,
  enabled: boolean = true,
  containerName?: string,
  sandboxGroupId?: string
) {
  return useQuery({
    queryKey: fileKeys.list(sandboxId, path, containerName),
    queryFn: () => fileService.listFiles(sandboxId, path, containerName, sandboxGroupId),
    enabled: enabled && !!sandboxId && !!path,
    staleTime: 10 * 1000,
  });
}

/**
 * Hook to get file/directory info
 */
export function useFileStat(
  sandboxId: string,
  path: string,
  enabled: boolean = true,
  containerName?: string,
  sandboxGroupId?: string
) {
  return useQuery({
    queryKey: fileKeys.stat(sandboxId, path, containerName),
    queryFn: () => fileService.stat(sandboxId, path, containerName, sandboxGroupId),
    enabled: enabled && !!sandboxId && !!path,
    staleTime: 10 * 1000,
  });
}

/**
 * Hook to upload/write a file
 */
export function useWriteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      path,
      content,
      createDirs,
      mode,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      path: string;
      content: Blob | File | string;
      createDirs?: boolean;
      mode?: number;
      containerName?: string;
      sandboxGroupId?: string;
    }) =>
      fileService.writeFile(
        sandboxId,
        path,
        content,
        { createDirs, mode, containerName },
        sandboxGroupId
      ),
    onSuccess: (_, { sandboxId, path, containerName }) => {
      const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
      queryClient.invalidateQueries({
        queryKey: fileKeys.list(sandboxId, parentPath, containerName),
      });
      toast.success('File uploaded successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to upload file: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a file or directory
 */
export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      path,
      recursive,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      path: string;
      recursive?: boolean;
      containerName?: string;
      sandboxGroupId?: string;
    }) => fileService.deleteFile(sandboxId, path, recursive, containerName, sandboxGroupId),
    onSuccess: (_, { sandboxId, path, containerName }) => {
      const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
      queryClient.invalidateQueries({
        queryKey: fileKeys.list(sandboxId, parentPath, containerName),
      });
      toast.success('Deleted successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to delete: ${error.message}`);
    },
  });
}

/**
 * Hook to create a directory
 */
export function useCreateDirectory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      path,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      path: string;
      containerName?: string;
      sandboxGroupId?: string;
    }) => fileService.mkdir(sandboxId, path, containerName, sandboxGroupId),
    onSuccess: (_, { sandboxId, path, containerName }) => {
      const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
      queryClient.invalidateQueries({
        queryKey: fileKeys.list(sandboxId, parentPath, containerName),
      });
      toast.success('Directory created successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create directory: ${error.message}`);
    },
  });
}

/**
 * Hook to download a file as blob (for in-app processing)
 */
export function useDownloadFile() {
  return useMutation({
    mutationFn: ({
      sandboxId,
      path,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      path: string;
      containerName?: string;
      sandboxGroupId?: string;
    }) => fileService.readFile(sandboxId, path, containerName, sandboxGroupId),
    onError: (error: Error) => {
      showErrorToast(`Failed to download file: ${error.message}`);
    },
  });
}
